import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider } from "./AppContext";
import Home from "./pages/Home";
import Marketing from "./pages/Marketing";
import { MarketingI18nProvider } from "./pages/marketing/MarketingLayout";
import MarketingHome from "./pages/marketing/MarketingHome";
import MarketingFeatures from "./pages/marketing/MarketingFeatures";
import MarketingPricing from "./pages/marketing/MarketingPricing";
import MarketingBlog from "./pages/marketing/MarketingBlog";
import MarketingDocs from "./pages/marketing/MarketingDocs";
import MarketingSignup from "./pages/marketing/MarketingSignup";
import AdminLogin from "./pages/AdminLogin";
import AdminPasswordReset from "./pages/AdminPasswordReset";
import AdminSetup from "./pages/AdminSetup";
import AdminDashboard from "./pages/AdminDashboard";
import OrderTracking from "./pages/OrderTracking";
import SignupSuccess from "./pages/SignupSuccess";
import DevBranding from "./components/DevBranding";

/**
 * Routing structure:
 *   /                       -> Marketing site (SaaS pitch + pricing + demo CTA)
 *                              EXCEPT on the legacy domain (gia-milly-eats.*) where it shows Home
 *   /r/:slug                -> Tenant storefront (uses /api/t/:slug/* endpoints)
 *   /r/:slug/track/:id      -> Tenant order tracking
 *   /admin/login | /admin   -> Legacy admin (still scoped to delicias-original)
 *   /track | /track/:id     -> Legacy storefront order tracking
 */

// Detect "legacy mode": the original Delicias domain (preview + production)
// should still see Home at /. New tenant SaaS storefronts use /r/{slug}.
const LEGACY_HOST_PATTERNS = [/gia-milly-eats/, /deliciasbistro\.com/];
const isLegacyDomain = () => {
    if (typeof window === "undefined") return true;
    return LEGACY_HOST_PATTERNS.some((re) => re.test(window.location.hostname));
};

const NotFound = () => (
    <div data-testid="not-found" className="min-h-screen flex flex-col items-center justify-center bg-earth-50 grain px-6 text-center">
        <div className="font-serif font-black text-7xl text-terracotta-500 mb-3">404</div>
        <div className="font-serif font-bold text-2xl text-earth-900 mb-2">
            ¡Ay, esa página no existe! / Page not found
        </div>
        <p className="text-earth-600 mb-7 max-w-md">
            Tal vez quieras volver a la cocina y pedir algo rico.
        </p>
        <Link to="/" className="btn-primary">
            Volver al inicio
        </Link>
        <div className="absolute bottom-4 left-0 right-0">
            <DevBranding variant="footer" className="text-center" />
        </div>
    </div>
);

function App() {
    const legacy = isLegacyDomain();
    return (
        <AppProvider>
            <div className="App">
                <BrowserRouter>
                    <Routes>
                        {/* Root: legacy domain shows Home; SaaS domain shows MarketingHome.
                            Force marketing on legacy with ?marketing=1 for previews. */}
                        <Route
                            path="/"
                            element={
                                legacy && typeof window !== "undefined" &&
                                !window.location.search.includes("marketing=1")
                                    ? <Home />
                                    : <MarketingI18nProvider><MarketingHome /></MarketingI18nProvider>
                            }
                        />

                        {/* HeavenlyEats marketing site (multi-page bilingual) */}
                        <Route path="/heavenly" element={<MarketingI18nProvider><MarketingHome /></MarketingI18nProvider>} />
                        <Route path="/heavenly/features" element={<MarketingI18nProvider><MarketingFeatures /></MarketingI18nProvider>} />
                        <Route path="/heavenly/pricing" element={<MarketingI18nProvider><MarketingPricing /></MarketingI18nProvider>} />
                        <Route path="/heavenly/blog" element={<MarketingI18nProvider><MarketingBlog /></MarketingI18nProvider>} />
                        <Route path="/heavenly/docs" element={<MarketingI18nProvider><MarketingDocs /></MarketingI18nProvider>} />
                        <Route path="/heavenly/signup" element={<MarketingI18nProvider><MarketingSignup /></MarketingI18nProvider>} />
                        {/* Legacy single-page marketing (kept for backwards compat) */}
                        <Route path="/heavenly-old" element={<Marketing />} />

                        {/* Signup success (Stripe redirect target) */}
                        <Route path="/signup/success" element={<SignupSuccess />} />

                        {/* SaaS tenant storefront */}
                        <Route path="/r/:slug" element={<Home />} />
                        <Route path="/r/:slug/track" element={<OrderTracking />} />
                        <Route path="/r/:slug/track/:id" element={<OrderTracking />} />

                        {/* Legacy admin + tracking */}
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/r/:slug/admin/login" element={<AdminLogin />} />
                        <Route path="/admin/reset-password" element={<AdminPasswordReset />} />
                        <Route path="/admin/setup/:token" element={<AdminSetup />} />
                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/track" element={<OrderTracking />} />
                        <Route path="/track/:id" element={<OrderTracking />} />

                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </BrowserRouter>
                <Toaster
                    position="top-right"
                    toastOptions={{
                        style: {
                            background: "#FAF7F2",
                            color: "#2C352B",
                            border: "1px solid #E6DED1",
                        },
                    }}
                />
            </div>
        </AppProvider>
    );
}

export default App;
